import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  BedDouble,
  Star,
  MapPin,
  Bus as BusIcon,
  FileCheck2,
  Send,
  Users,
  Palmtree,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  Utensils,
  ShieldCheck,
  ArrowDownUp,
  X,
} from "lucide-react";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import HotelDetailModal from "@/components/HotelDetailModal";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { PACKAGES, BUS_ROUTES, formatINR, CONTACT } from "@/mock";
import { apiCreateEnquiry, apiHotelSuggest, apiSearchHotels, apiHotelPricing, apiHotelBook, apiAutoRefund, apiGuestCheckout, apiOtpVerify } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { TravellerSuggest, ContactDatalists, useSavedContacts, refreshSavedTravellers } from "@/components/TravellerSuggest";
import { FlightCrossSell } from "@/components/CrossSell";
import { useCart } from "@/context/CartContext";
import { payWithRazorpay } from "@/lib/razorpay";
import { downloadReceiptPdf } from "@/lib/invoicePdf";
import { toast } from "sonner";

const PageShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-[#F5F7FB]">
    <TopBar />
    <Header />
    <div className="bg-gradient-to-b from-[#EAF0FF] to-[#F5F7FB] py-10">
      <div className="mx-auto max-w-7xl px-4 text-center">
        <h1 className="font-[Poppins] text-3xl font-extrabold text-[#1B1F5B]">{title}</h1>
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      </div>
    </div>
    <div className="mx-auto max-w-7xl px-4 py-8">{children}</div>
    <Footer />
  </div>
);

// ---------------- Hotels (TripJack live) ----------------
const iso = (d) => d.toISOString().slice(0, 10);

const HotelImages = ({ images, name, hotelId }) => {
  const [idx, setIdx] = useState(0);
  const list = (images || []).filter(Boolean);
  if (!list.length) {
    return (
      <div className="relative h-44 overflow-hidden bg-gradient-to-br from-[#1B1F5B]/10 to-[#F5B32B]/10">
        <div className="absolute inset-0 flex items-center justify-center"><BedDouble className="h-10 w-10 text-[#1B1F5B]/30" /></div>
      </div>
    );
  }
  const cur = Math.min(idx, list.length - 1);
  return (
    <div className="group relative h-44 overflow-hidden bg-gradient-to-br from-[#1B1F5B]/10 to-[#F5B32B]/10">
      <div className="absolute inset-0 flex items-center justify-center"><BedDouble className="h-10 w-10 text-[#1B1F5B]/30" /></div>
      <img key={list[cur]} src={list[cur]} alt={name} loading="lazy"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        className="absolute inset-0 h-full w-full object-cover" />
      {list.length > 1 && (
        <>
          <button data-testid={`hotel-photo-prev-${hotelId}`}
            onClick={(e) => { e.stopPropagation(); setIdx((cur - 1 + list.length) % list.length); }}
            className="absolute left-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition-opacity hover:bg-black/65 group-hover:opacity-100">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button data-testid={`hotel-photo-next-${hotelId}`}
            onClick={(e) => { e.stopPropagation(); setIdx((cur + 1) % list.length); }}
            className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition-opacity hover:bg-black/65 group-hover:opacity-100">
            <ChevronRight className="h-4 w-4" />
          </button>
          <span data-testid={`hotel-photo-count-${hotelId}`}
            className="absolute bottom-1.5 right-1.5 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-bold text-white">
            {cur + 1}/{list.length}
          </span>
        </>
      )}
    </div>
  );
};

const Counter = ({ label, value, onChange, min = 0, testid }) => (
  <div className="flex min-w-0 items-center justify-between gap-1 rounded-xl border border-slate-200 bg-white px-2 py-2">
    <span className="truncate text-[11px] font-semibold text-slate-500">{label}</span>
    <div className="flex shrink-0 items-center gap-1">
      <button type="button" data-testid={`${testid}-minus`} onClick={() => onChange(Math.max(min, value - 1))}
        className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-sm font-bold text-slate-600 hover:bg-slate-200">-</button>
      <span className="w-4 text-center text-sm font-extrabold text-[#1B1F5B]">{value}</span>
      <button type="button" data-testid={`${testid}-plus`} onClick={() => onChange(value + 1)}
        className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-sm font-bold text-slate-600 hover:bg-slate-200">+</button>
    </div>
  </div>
);

export const HotelPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setSession } = useAuth();
  const { add: addToCart } = useCart();
  const [query, setQuery] = useState("");
  const [city, setCity] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [checkIn, setCheckIn] = useState(iso(new Date(Date.now() + 30 * 864e5)));
  const [checkOut, setCheckOut] = useState(iso(new Date(Date.now() + 31 * 864e5)));
  const [rooms, setRooms] = useState(1);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [state, setState] = useState({ status: "idle", hotels: [], nights: 1, error: "" });
  const [expanded, setExpanded] = useState(null); // hotelId with loaded options
  const [detailHotel, setDetailHotel] = useState(null); // MMT-style room detail modal
  const [options, setOptions] = useState({});
  const [optionsLoading, setOptionsLoading] = useState(null);
  const [bookingDraft, setBookingDraft] = useState(null); // {hotel, option}
  const [guest, setGuest] = useState({ name: "", email: "", phone: "", pan: "", passportNumber: "", passportExpiry: "", nationality: "Indian" });
  const [travellers, setTravellers] = useState([]);
  const [booked, setBooked] = useState(null);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [otpFlow, setOtpFlow] = useState(null); // { email } when existing account needs OTP
  const [otpCode, setOtpCode] = useState("");
  const [starFilter, setStarFilter] = useState(0); // 0 = all
  const [freeCancelOnly, setFreeCancelOnly] = useState(false);
  const [breakfastOnly, setBreakfastOnly] = useState(false);
  const [hotelSort, setHotelSort] = useState(""); // "" | price-asc | price-desc
  const [hotelFiltersOpen, setHotelFiltersOpen] = useState(false);
  const sugTimer = useRef(null);

  const HOTEL_FILTER_STORE = "ft_hotel_filters";
  const skipSaveHotel = useRef(true);
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(HOTEL_FILTER_STORE) || "{}");
      if (typeof s.starFilter === "number") setStarFilter(s.starFilter);
      if (typeof s.freeCancelOnly === "boolean") setFreeCancelOnly(s.freeCancelOnly);
      if (typeof s.breakfastOnly === "boolean") setBreakfastOnly(s.breakfastOnly);
      if (typeof s.hotelSort === "string") setHotelSort(s.hotelSort);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (skipSaveHotel.current) { skipSaveHotel.current = false; return; }
    try {
      localStorage.setItem(HOTEL_FILTER_STORE, JSON.stringify({ starFilter, freeCancelOnly, breakfastOnly, hotelSort }));
    } catch { /* ignore */ }
  }, [starFilter, freeCancelOnly, breakfastOnly, hotelSort]);

  const hasBreakfast = (m) => /breakfast|bed and breakfast|half board|full board|all meals/i.test(m || "");
  const shownHotels = state.hotels
    .filter((h) => !starFilter || h.star === starFilter)
    .filter((h) => !freeCancelOnly || h.freeCancellation)
    .filter((h) => !breakfastOnly || hasBreakfast(h.mealBasis))
    .sort((a, b) => {
      if (hotelSort === "price-asc") return a.price - b.price;
      if (hotelSort === "price-desc") return b.price - a.price;
      return 0;
    });
  const hotelFilterCount = (starFilter ? 1 : 0) + (freeCancelOnly ? 1 : 0) + (breakfastOnly ? 1 : 0) + (hotelSort ? 1 : 0);

  // Resume a pending hotel booking after login
  useEffect(() => {
    if (!user) return;
    const pending = sessionStorage.getItem("ft_hotel_pending");
    if (pending) {
      try {
        const d = JSON.parse(pending);
        setBookingDraft(d);
        setGuest({ name: user.name || "", email: user.email || "", phone: user.phone || "", pan: "", passportNumber: "", passportExpiry: "", nationality: "Indian" });
      } catch { /* ignore */ }
    }
  }, [user]);

  // Auto-load New Delhi live hotels on first open so the page never looks empty
  useEffect(() => {
    doSearch({ kind: "city", regionId: 725862, name: "Delhi", label: "Delhi, India" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build one traveller card per pax, room-major order (mirrors backend rooms_spec)
  useEffect(() => {
    if (!bookingDraft) return;
    const { rooms: r, adults: a, children: c } = bookingDraft;
    const list = [];
    for (let i = 0; i < r; i++) {
      const na = Math.max(Math.floor(a / r) + (i < a % r ? 1 : 0), 1);
      for (let j = 0; j < na; j++) list.push({ ti: "Mr", fN: "", lN: "", pt: "ADULT", room: i + 1, label: `Adult ${j + 1}` });
      if (i === 0) for (let j = 0; j < c; j++) list.push({ ti: "Master", fN: "", lN: "", pt: "CHILD", room: 1, label: `Child ${j + 1}` });
    }
    if (list.length && user?.name) {
      const parts = user.name.trim().split(/\s+/);
      list[0].fN = parts[0];
      list[0].lN = parts.slice(1).join(" ");
    }
    setTravellers(list);
  }, [bookingDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCityType = (v) => {
    setQuery(v);
    setCity(null);
    clearTimeout(sugTimer.current);
    if (v.trim().length < 2) { setSuggestions([]); return; }
    sugTimer.current = setTimeout(async () => {
      try {
        const res = await apiHotelSuggest(v.trim());
        setSuggestions(res.suggestions || []);
        setShowSug(true);
      } catch { setSuggestions([]); }
    }, 250);
  };

  const doSearch = async (sel) => {
    let c = sel || city;
    // If user typed a name and hit Search without picking, auto-pick the top suggestion
    if (!c && query.trim().length >= 2) {
      try {
        const res = await apiHotelSuggest(query.trim());
        if (res.suggestions?.length) {
          const s = res.suggestions[0];
          c = { ...s, label: s.subtitle ? `${s.name}, ${s.subtitle}` : s.name };
        }
      } catch { /* ignore */ }
    }
    if (!c) return toast.error("Please select a city or hotel from the dropdown");
    setCity(c);
    if (!checkIn || !checkOut || checkOut <= checkIn)
      return toast.error("Check-out must be after check-in");
    setState({ status: "loading", hotels: [], nights: 1, error: "" });
    setExpanded(null); setOptions({});
    try {
      const payload = { checkIn, checkOut, rooms, adults, children };
      if (c.kind === "hotel") payload.hotelId = c.hotelId;
      else payload.cityRegionId = c.regionId;
      const res = await apiSearchHotels(payload);
      if (res.source === "tripjack") {
        setState({ status: "ok", hotels: res.hotels, nights: res.nights, error: "" });
      } else if (res.source === "no_hotels") {
        setState({ status: "empty", hotels: [], nights: 1, error: "" });
      } else {
        setState({ status: "error", hotels: [], nights: 1, error: res.provider_error || "Live hotel search failed" });
      }
    } catch {
      setState({ status: "error", hotels: [], nights: 1, error: "Live hotel service unreachable. Please retry." });
    }
  };

  const toggleRooms = async (h) => {
    setDetailHotel(h);
    if (options[h.hotelId]) return;
    setOptionsLoading(h.hotelId);
    try {
      const res = await apiHotelPricing({ hotelId: h.hotelId, checkIn, checkOut, rooms, adults, children });
      if (res.source === "tripjack") {
        setOptions((p) => ({ ...p, [h.hotelId]: { list: res.options, reviewHash: res.reviewHash, correlationId: res.correlationId } }));
      } else {
        toast.error(res.provider_error || "Could not load rooms");
        setDetailHotel(null);
      }
    } catch {
      toast.error("Could not load room options");
      setDetailHotel(null);
    } finally { setOptionsLoading(null); }
  };

  const startBooking = (hotel, option, meta = {}) => {
    const draft = { hotel, option, reviewHash: meta.reviewHash || "", correlationId: meta.correlationId || "",
                    city: city?.label || hotel.city, checkIn, checkOut, rooms, adults, children };
    // MMT-style guest checkout: no forced login. Collect guest details in the dialog,
    // auto-create/attach an account at confirm time, then pay.
    setOtpFlow(null); setOtpCode("");
    setGuest({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "", pan: "", passportNumber: "", passportExpiry: "", nationality: "Indian" });
    setBookingDraft(draft);
  };

  const confirmBooking = async () => {
    for (let i = 0; i < travellers.length; i++) {
      if (!travellers[i].fN.trim() || !travellers[i].lN.trim())
        return toast.error(`Please fill first & last name for traveller ${i + 1}`);
    }
    if (!guest.email || !guest.phone)
      return toast.error("Please fill lead guest email & phone");
    const opt = bookingDraft.option;
    if (opt.panRequired) {
      if (!guest.pan.trim()) return toast.error("PAN is mandatory for this hotel rate");
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(guest.pan.trim().toUpperCase()))
        return toast.error("Invalid PAN format (e.g. ABCDE1234F)");
    }
    if (opt.passportRequired && (!guest.passportNumber.trim() || !guest.passportExpiry || !guest.nationality.trim()))
      return toast.error("Passport number, expiry date & nationality are mandatory for this hotel rate");
    // MMT-style guest checkout: create/attach an account from the entered details.
    // New email -> instant account; existing email -> OTP to claim it (no password shown).
    if (!user) {
      setBookingBusy(true);
      try {
        const g = await apiGuestCheckout({
          name: `${travellers[0]?.fN || ""} ${travellers[0]?.lN || ""}`.trim() || guest.email.split("@")[0],
          email: guest.email.trim(), phone: guest.phone.trim(),
        });
        if (g.otp_required) {
          setOtpFlow({ email: guest.email.trim() });
          setOtpCode("");
          setBookingBusy(false);
          toast.info("Is email par pehle se bookings hain — humne OTP bheja hai");
          return;
        }
        setSession(g.token, g.user);
        toast.success("Account ready! Aapka hotel voucher isi email par save hoga.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not start guest checkout");
        setBookingBusy(false);
        return;
      }
    } else {
      setBookingBusy(true);
    }
    let paymentId = null;
    try {
      const { hotel, option } = bookingDraft;
      // Customers pay via Razorpay before booking; agents pay from wallet at book time.
      if (user?.role !== "agent") {
        const pay = await payWithRazorpay({
          amount: Math.round(option.totalPrice), purpose: "hotel",
          contact: { name: `${travellers[0]?.fN || ""} ${travellers[0]?.lN || ""}`.trim(), email: guest.email, phone: guest.phone },
          description: `Hotel ${hotel.name}`,
        });
        if (!pay?.paymentId) { setBookingBusy(false); return; }
        paymentId = pay.paymentId;
      }
      const res = await apiHotelBook({
        hotel: { hotelId: hotel.hotelId, name: hotel.name, address: hotel.address, image: hotel.image, star: hotel.star },
        city: bookingDraft.city, checkIn: bookingDraft.checkIn, checkOut: bookingDraft.checkOut,
        rooms: bookingDraft.rooms, adults: bookingDraft.adults, children: bookingDraft.children,
        roomName: option.roomName, mealBasis: option.mealBasis,
        optionId: option.optionId, reviewHash: bookingDraft.reviewHash, correlationId: bookingDraft.correlationId,
        guest: { ...guest, name: `${travellers[0]?.fN || ""} ${travellers[0]?.lN || ""}`.trim() },
        travellers: travellers.map(({ ti, fN, lN, pt }) => ({ ti, fN: fN.trim(), lN: lN.trim(), pt })),
        totalPrice: option.totalPrice,
        razorpayPaymentId: paymentId,
      });
      sessionStorage.removeItem("ft_hotel_pending");
      setBooked(res.booking); refreshSavedTravellers();
      setBookingDraft(null);
      toast.success(`Hotel booked! Reference ${res.booking.pnr}`);
    } catch (e) {
      if (paymentId) {
        try {
          await apiAutoRefund({ paymentId, reason: e?.response?.data?.detail || "Hotel booking failed at supplier" });
          toast.success("Booking issue hua — aapka payment auto-refund ho gaya hai (5-7 working days)");
        } catch {
          toast.error("Booking failed. Refund support se process hoga.");
        }
      }
      toast.error(e?.response?.data?.detail || "Booking failed. Please try again.");
    } finally { setBookingBusy(false); }
  };

  const verifyHotelOtp = async () => {
    if (otpCode.trim().length !== 6) { toast.error("Enter the 6-digit OTP from your email"); return; }
    setBookingBusy(true);
    try {
      const res = await apiOtpVerify({ email: otpFlow.email, otp: otpCode.trim() });
      setSession(res.token, res.user);
      setOtpFlow(null);
      setOtpCode("");
      toast.success("Verified! Ab 'Confirm Booking' dabakar payment karein — sari trips isi email par save rahengi.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP verification failed");
    } finally {
      setBookingBusy(false);
    }
  };

  return (
    <PageShell title="Book Hotels at Best Prices" subtitle="Live prices from TripJack across hotels worldwide">
      <div className="mx-auto mb-8 max-w-4xl rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[1.4fr_1fr_1fr_auto]">
          <div className="relative">
            <input
              data-testid="hotel-city-input"
              value={city ? city.label : query}
              onChange={(e) => onCityType(e.target.value)}
              onFocus={() => suggestions.length && setShowSug(true)}
              placeholder="City, area ya hotel name (e.g. Delhi, Taj Palace)"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]"
            />
            {showSug && suggestions.length > 0 && (
              <div data-testid="hotel-city-suggestions" className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-slate-100 bg-white shadow-xl">
                {suggestions.map((c, i) => {
                  const label = c.subtitle ? `${c.name}, ${c.subtitle}` : c.name;
                  return (
                    <button key={`${c.kind}-${c.hotelId || c.regionId}-${i}`} data-testid={`hotel-city-option-${c.hotelId || c.regionId}`}
                      onClick={() => { setCity({ ...c, label }); setQuery(label); setShowSug(false); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left hover:bg-slate-50">
                      {c.kind === "hotel"
                        ? <BedDouble className="h-3.5 w-3.5 shrink-0 text-[#F5B32B]" />
                        : <MapPin className="h-3.5 w-3.5 shrink-0 text-[#1B1F5B]" />}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-bold text-slate-700">{c.name}</span>
                        {c.subtitle && <span className="block truncate text-[10px] text-slate-400">{c.subtitle}</span>}
                      </span>
                      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-extrabold uppercase ${c.kind === "hotel" ? "bg-[#FFF9EC] text-[#B57A00]" : "bg-slate-100 text-slate-500"}`}>
                        {c.kind === "hotel" ? "Hotel" : c.kind === "city" ? "City" : "Area"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <input data-testid="hotel-checkin-input" type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
            className="rounded-xl border border-slate-200 px-3 py-3 text-sm text-slate-600 outline-none focus:border-[#1B1F5B]" />
          <input data-testid="hotel-checkout-input" type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)}
            className="rounded-xl border border-slate-200 px-3 py-3 text-sm text-slate-600 outline-none focus:border-[#1B1F5B]" />
          <button data-testid="hotel-search-btn" onClick={() => doSearch()} disabled={state.status === "loading"}
            className="rounded-xl bg-[#1B1F5B] px-8 py-3 text-sm font-bold text-white hover:bg-[#141844] disabled:opacity-60">
            {state.status === "loading" ? "Searching..." : "Search"}
          </button>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <Counter label="Rooms" value={rooms} onChange={setRooms} min={1} testid="hotel-rooms" />
          <Counter label="Adults" value={adults} onChange={setAdults} min={1} testid="hotel-adults" />
          <Counter label="Children" value={children} onChange={setChildren} min={0} testid="hotel-children" />
        </div>
      </div>

      {state.status === "ok" && (
        <div className="mb-4 flex items-center gap-2" data-testid="hotel-live-badge">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
          </span>
          <span className="text-xs font-bold text-emerald-600">Live prices by TripJack</span>
          <span className="text-xs text-slate-400">
            {state.hotels.length} hotels in {city?.label} | {state.nights} night(s)
          </span>
        </div>
      )}

      <FlightCrossSell city={city?.label} />

      {state.status === "error" && (
        <div data-testid="hotel-error" className="mx-auto max-w-lg rounded-2xl border border-red-100 bg-red-50 p-6 text-center">
          <p className="text-sm font-bold text-red-600">Live hotel prices temporarily unavailable</p>
          <p className="mt-1 text-xs text-red-400">{state.error}</p>
          <button data-testid="hotel-retry-btn" onClick={() => doSearch()}
            className="mt-3 rounded-lg bg-[#1B1F5B] px-5 py-2 text-xs font-bold text-white">Retry Search</button>
        </div>
      )}

      {state.status === "loading" && (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="hotel-loading">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
              <div className="h-44 animate-pulse bg-slate-100" />
              <div className="space-y-2.5 p-4">
                <div className="h-3.5 w-3/4 animate-pulse rounded bg-slate-100" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="h-2.5 w-2/3 animate-pulse rounded bg-slate-100" />
                <div className="h-5 w-1/3 animate-pulse rounded bg-slate-100 pt-2" />
              </div>
            </div>
          ))}
          <p className="col-span-full text-center text-xs font-semibold text-slate-400">
            Fetching live hotel prices from TripJack... (may take 20-30 seconds)
          </p>
        </div>
      )}

      {state.status === "empty" && (
        <p data-testid="hotel-empty" className="py-14 text-center text-sm text-slate-400">
          No live hotels found for {city?.label} on these dates. Try different dates.
        </p>
      )}

      {state.status === "ok" && (
        <>
          {/* Desktop: inline filter bar */}
          <div className="mb-4 hidden flex-wrap items-center gap-2 md:flex" data-testid="hotel-star-filter">
            <span className="text-xs font-semibold text-slate-400">Star Rating:</span>
            {[0, 5, 4, 3, 2, 1].map((s) => (
              <button key={s} data-testid={`hotel-star-${s || "all"}`} onClick={() => setStarFilter(s)}
                className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold transition-colors ${
                  starFilter === s
                    ? "border-[#1B1F5B] bg-[#1B1F5B] text-white"
                    : "border-slate-200 bg-white text-slate-500 hover:border-[#1B1F5B]/40"
                }`}>
                {s === 0 ? "All" : <>{s} <Star className="h-3 w-3 fill-amber-400 text-amber-400" /></>}
              </button>
            ))}
            <span className="mx-1 h-4 w-px bg-slate-200" />
            <button data-testid="hotel-filter-breakfast" onClick={() => setBreakfastOnly((v) => !v)}
              className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold transition-colors ${breakfastOnly ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-200 bg-white text-slate-500 hover:border-emerald-400"}`}>
              <Utensils className="h-3 w-3" /> Breakfast
            </button>
            <button data-testid="hotel-filter-freecancel" onClick={() => setFreeCancelOnly((v) => !v)}
              className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold transition-colors ${freeCancelOnly ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-200 bg-white text-slate-500 hover:border-emerald-400"}`}>
              <ShieldCheck className="h-3 w-3" /> Free cancellation
            </button>
            <span className="mx-1 h-4 w-px bg-slate-200" />
            <button data-testid="hotel-sort-price" onClick={() => setHotelSort(hotelSort === "price-asc" ? "price-desc" : hotelSort === "price-desc" ? "" : "price-asc")}
              className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold transition-colors ${hotelSort ? "border-[#1B1F5B] bg-[#1B1F5B] text-white" : "border-slate-200 bg-white text-slate-500 hover:border-[#1B1F5B]/40"}`}>
              <ArrowDownUp className="h-3 w-3" /> {hotelSort === "price-desc" ? "Price: High-Low" : hotelSort === "price-asc" ? "Price: Low-High" : "Sort by Price"}
            </button>
            {hotelFilterCount > 0 && (
              <span className="text-xs text-slate-400">{shownHotels.length} of {state.hotels.length} hotels</span>
            )}
          </div>

          {/* Mobile: filter trigger button */}
          <div className="mb-3 md:hidden">
            <button data-testid="hotel-mobile-filters-btn" onClick={() => setHotelFiltersOpen(true)}
              className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-[#1B1F5B] shadow-sm active:scale-[0.99]">
              <span className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4" /> Filters & Sort
                {hotelFilterCount > 0 && (
                  <span data-testid="hotel-mobile-filters-count" className="rounded-full bg-[#1B1F5B] px-2 py-0.5 text-[10px] font-extrabold text-white">{hotelFilterCount}</span>
                )}
              </span>
              <span className="text-xs font-semibold text-slate-400">{shownHotels.length} hotels</span>
            </button>
          </div>

          {/* Mobile: MMT-style bottom sheet */}
          <Drawer open={hotelFiltersOpen} onOpenChange={setHotelFiltersOpen}>
            <DrawerContent data-testid="hotel-mobile-filters-panel" className="max-h-[85vh]">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 pb-3 pt-2">
                <DrawerTitle className="flex items-center gap-2 text-base font-extrabold text-[#1B1F5B]">
                  <SlidersHorizontal className="h-4 w-4" /> Filters & Sort
                </DrawerTitle>
                <button data-testid="hotel-mobile-filters-close" onClick={() => setHotelFiltersOpen(false)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="overflow-y-auto px-5 py-4">
                <p className="mb-2 text-[13px] font-bold text-slate-700">Star Rating</p>
                <div className="flex flex-wrap gap-2">
                  {[0, 5, 4, 3, 2, 1].map((s) => (
                    <button key={s} data-testid={`hotel-m-star-${s || "all"}`} onClick={() => setStarFilter(s)}
                      className={`flex items-center gap-1 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${starFilter === s ? "border-[#1B1F5B] bg-[#1B1F5B] text-white" : "border-slate-200 bg-white text-slate-500"}`}>
                      {s === 0 ? "All" : <>{s} <Star className="h-3 w-3 fill-amber-400 text-amber-400" /></>}
                    </button>
                  ))}
                </div>
                <p className="mb-2 mt-5 text-[13px] font-bold text-slate-700">Popular Filters</p>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="hotel-m-breakfast" onClick={() => setBreakfastOnly((v) => !v)}
                    className={`flex items-center gap-1 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${breakfastOnly ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-200 bg-white text-slate-500"}`}>
                    <Utensils className="h-3 w-3" /> Breakfast Included
                  </button>
                  <button data-testid="hotel-m-freecancel" onClick={() => setFreeCancelOnly((v) => !v)}
                    className={`flex items-center gap-1 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${freeCancelOnly ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-200 bg-white text-slate-500"}`}>
                    <ShieldCheck className="h-3 w-3" /> Free Cancellation
                  </button>
                </div>
                <p className="mb-2 mt-5 text-[13px] font-bold text-slate-700">Sort By</p>
                <div className="flex flex-wrap gap-2">
                  {[{ id: "", l: "Recommended" }, { id: "price-asc", l: "Price: Low to High" }, { id: "price-desc", l: "Price: High to Low" }].map((o) => (
                    <button key={o.id || "rec"} data-testid={`hotel-m-sort-${o.id || "rec"}`} onClick={() => setHotelSort(o.id)}
                      className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${hotelSort === o.id ? "border-[#1B1F5B] bg-[#1B1F5B] text-white" : "border-slate-200 bg-white text-slate-500"}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 border-t border-slate-100 p-4">
                <button data-testid="hotel-mobile-filters-clear"
                  onClick={() => { setStarFilter(0); setFreeCancelOnly(false); setBreakfastOnly(false); setHotelSort(""); }}
                  className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-600">Clear All</button>
                <button data-testid="hotel-mobile-filters-apply" onClick={() => setHotelFiltersOpen(false)}
                  className="flex-[2] rounded-xl bg-[#1B1F5B] py-3 text-sm font-extrabold text-white">Show {shownHotels.length} hotel{shownHotels.length !== 1 ? "s" : ""}</button>
              </div>
            </DrawerContent>
          </Drawer>
        </>
      )}

      {state.status === "ok" && shownHotels.length === 0 && (
        <p data-testid="hotel-filter-empty" className="py-10 text-center text-sm text-slate-400">
          No hotels match these filters. Try adjusting your selection.
        </p>
      )}

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {shownHotels.map((h) => (
          <div key={h.hotelId} data-testid={`hotel-card-${h.hotelId}`}
            className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
            <HotelImages images={h.images && h.images.length ? h.images : (h.image ? [h.image] : [])} name={h.name} hotelId={h.hotelId} />
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-bold text-slate-800" data-testid={`hotel-name-${h.hotelId}`}>{h.name}</h3>
                {h.star && (
                  <span className="flex shrink-0 items-center gap-0.5 rounded-md bg-amber-50 px-1.5 py-0.5 text-[11px] font-bold text-amber-600">
                    <Star className="h-3 w-3 fill-amber-500 text-amber-500" /> {h.star}
                  </span>
                )}
              </div>
              <p className="mt-1 flex items-center gap-1 text-xs font-bold text-[#1B1F5B]" data-testid={`hotel-city-${h.hotelId}`}>
                <MapPin className="h-3 w-3 shrink-0 text-[#F5B32B]" />
                {[h.city, h.country].filter(Boolean).join(", ") || city?.label}
              </p>
              {h.address && (
                <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-slate-400" title={h.address}>
                  {h.address}
                </p>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {h.mealBasis && <span className="rounded-md bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{h.mealBasis}</span>}
                {h.freeCancellation && <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">Free Cancellation</span>}
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-dashed pt-3">
                <p className="text-base font-extrabold text-[#1B1F5B]">
                  {formatINR(h.price)}
                  <span className="block text-[10px] font-medium text-slate-400">
                    {formatINR(h.pricePerNight)}/night x {state.nights} night(s)
                  </span>
                </p>
                <button data-testid={`hotel-view-rooms-${h.hotelId}`} onClick={() => toggleRooms(h)}
                  className="rounded-lg bg-[#F5B32B] px-3.5 py-1.5 text-xs font-extrabold text-[#0A0F2E] hover:bg-[#E89A10]">
                  View Rooms
                </button>
              </div>
              <button
                data-testid={`hotel-add-cart-${h.hotelId}`}
                onClick={() => addToCart({
                  kind: "hotel",
                  title: h.name,
                  route: `${h.name}, ${[h.city, h.country].filter(Boolean).join(", ") || city?.label || ""}`,
                  date: checkIn,
                  price: h.price,
                  pax: adults + children,
                  meta: { hotelId: h.hotelId, city: city?.label, checkIn, checkOut, rooms },
                })}
                className="mt-2 w-full rounded-lg border border-[#1B1F5B]/20 py-1.5 text-xs font-bold text-[#1B1F5B] transition-colors hover:bg-[#1B1F5B]/5">
                + Add to Trip
              </button>
            </div>
          </div>
        ))}
      </div>

      {detailHotel && (
        <HotelDetailModal
          hotel={detailHotel}
          nights={state.nights}
          options={options[detailHotel.hotelId]}
          loading={optionsLoading === detailHotel.hotelId}
          onBook={(hotel, option) => { setDetailHotel(null); startBooking(hotel, option, options[hotel.hotelId]); }}
          onClose={() => setDetailHotel(null)}
        />
      )}

      {bookingDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="hotel-booking-dialog">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-extrabold text-[#1B1F5B]">{bookingDraft.hotel.name}</h3>
            <p className="mt-0.5 text-xs text-slate-400">
              {bookingDraft.checkIn} to {bookingDraft.checkOut} | {bookingDraft.rooms} room(s) | {bookingDraft.option.roomName} | {bookingDraft.option.mealBasis}
            </p>
            <p className="mt-2 text-xl font-extrabold text-[#1B1F5B]">{formatINR(bookingDraft.option.totalPrice)}</p>
            <p className="text-[10px] font-semibold text-sky-600">Live booking via TripJack - real confirmation on success</p>
            <div className="mt-4 space-y-2.5">
              <div className="max-h-60 space-y-2 overflow-y-auto pr-1" data-testid="hotel-travellers-list">
                {travellers.map((t, i) => (
                  <div key={i} className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5" data-testid={`hotel-traveller-${i}`}>
                    <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                      Room {t.room} · {t.label}{i === 0 ? " · Lead Guest" : ""}
                    </p>
                    <div className="grid grid-cols-[72px_1fr_1fr] gap-1.5">
                      <select data-testid={`hotel-traveller-${i}-title`} value={t.ti}
                        onChange={(e) => setTravellers(travellers.map((x, xi) => (xi === i ? { ...x, ti: e.target.value } : x)))}
                        className="rounded-lg border border-slate-200 bg-white px-1.5 py-2 text-xs outline-none focus:border-[#1B1F5B]">
                        {(t.pt === "CHILD" ? ["Master", "Miss"] : ["Mr", "Mrs", "Ms"]).map((o) => <option key={o}>{o}</option>)}
                      </select>
                      <TravellerSuggest testId={`hotel-traveller-${i}-fname`} value={t.fN} type={t.pt}
                        onChange={(v) => setTravellers(travellers.map((x, xi) => (xi === i ? { ...x, fN: v } : x)))}
                        onPick={(tr) => {
                          setTravellers(travellers.map((x, xi) => (xi === i ? { ...x, ti: tr.title || x.ti, fN: tr.firstName, lN: tr.lastName } : x)));
                          if (i === 0) setGuest((g) => ({ ...g, ...(user?.role === "agent" && tr.email ? { email: tr.email, phone: tr.phone || g.phone } : {}),
                            pan: tr.pan || g.pan, passportNumber: tr.passport || g.passportNumber, passportExpiry: tr.passportExpiry || g.passportExpiry }));
                        }}
                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs outline-none focus:border-[#1B1F5B]" />
                      <input data-testid={`hotel-traveller-${i}-lname`} value={t.lN} placeholder="Last name"
                        onChange={(e) => setTravellers(travellers.map((x, xi) => (xi === i ? { ...x, lN: e.target.value } : x)))}
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs outline-none focus:border-[#1B1F5B]" />
                    </div>
                  </div>
                ))}
              </div>
              <ContactDatalists /><input data-testid="hotel-guest-email" type="email" list={user?.role === "agent" ? "saved-emails" : undefined} value={guest.email} onChange={(e) => { setGuest({ ...guest, email: e.target.value }); if (otpFlow) { setOtpFlow(null); setOtpCode(""); } }}
                placeholder="Email (confirmation will be sent here)" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#1B1F5B]" />
              <input data-testid="hotel-guest-phone" list={user?.role === "agent" ? "saved-phones" : undefined} value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })}
                placeholder="Mobile number" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#1B1F5B]" />
              {bookingDraft.option.panRequired && (
                <div>
                  <input data-testid="hotel-guest-pan" value={guest.pan} maxLength={10}
                    onChange={(e) => setGuest({ ...guest, pan: e.target.value.toUpperCase() })}
                    placeholder="PAN number (mandatory for this rate)"
                    className="w-full rounded-xl border border-amber-300 bg-amber-50/50 px-4 py-2.5 text-sm uppercase tracking-wider outline-none focus:border-[#1B1F5B]" />
                  <p className="mt-1 text-[10px] font-semibold text-amber-600">Format: ABCDE1234F - lead guest PAN required by hotel</p>
                </div>
              )}
              {bookingDraft.option.passportRequired && (
                <div className="space-y-2.5 rounded-xl border border-amber-200 bg-amber-50/40 p-3" data-testid="hotel-passport-section">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-amber-600">Passport details mandatory for this rate (lead guest)</p>
                  <input data-testid="hotel-guest-passport-number" value={guest.passportNumber}
                    onChange={(e) => setGuest({ ...guest, passportNumber: e.target.value.toUpperCase() })}
                    placeholder="Passport number" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm uppercase outline-none focus:border-[#1B1F5B]" />
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="hotel-guest-passport-expiry" type="date" value={guest.passportExpiry}
                      onChange={(e) => setGuest({ ...guest, passportExpiry: e.target.value })}
                      title="Passport expiry date" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#1B1F5B]" />
                    <input data-testid="hotel-guest-nationality" value={guest.nationality}
                      onChange={(e) => setGuest({ ...guest, nationality: e.target.value })}
                      placeholder="Nationality" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#1B1F5B]" />
                  </div>
                </div>
              )}
            </div>
            {!user && (
              <div data-testid="hotel-guest-note" className="mt-3 rounded-xl border border-[#0E7C6B]/20 bg-[#0E7C6B]/5 px-3 py-2 text-[11px] font-semibold text-[#0E7C6B]">
                No login needed — hum aapke email par account bana denge aur voucher/booking wahi save rahegi. Baad mein email OTP se access karein.
              </div>
            )}
            {otpFlow && (
              <div data-testid="hotel-otp-box" className="mt-3 rounded-xl border border-[#1B1F5B]/20 bg-[#F3F6FF] p-4">
                <p className="text-xs font-bold text-[#1B1F5B]">OTP sent to {otpFlow.email}</p>
                <p className="mt-0.5 text-[11px] text-slate-500">Is email par aapki purani bookings hain. OTP daalkar securely continue karein.</p>
                <div className="mt-2 flex gap-2">
                  <input data-testid="hotel-otp-input" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit OTP" inputMode="numeric"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm tracking-widest outline-none focus:border-[#1B1F5B]" />
                  <button data-testid="hotel-otp-verify-btn" onClick={verifyHotelOtp} disabled={bookingBusy}
                    className="rounded-lg bg-[#1B1F5B] px-5 py-2 text-sm font-bold text-white hover:bg-[#141844] disabled:opacity-60">Verify</button>
                </div>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button data-testid="hotel-cancel-book-btn" onClick={() => { setBookingDraft(null); setOtpFlow(null); setOtpCode(""); sessionStorage.removeItem("ft_hotel_pending"); }}
                className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-bold text-slate-500">Cancel</button>
              <button data-testid="hotel-confirm-book-btn" onClick={confirmBooking} disabled={bookingBusy || !!otpFlow}
                className="flex-1 rounded-xl bg-[#1B1F5B] py-2.5 text-sm font-extrabold text-white hover:bg-[#141844] disabled:opacity-60">
                {bookingBusy ? "Booking..." : user?.role === "agent" ? "Pay from Wallet" : !user ? "Continue & Pay" : "Confirm Booking"}
              </button>
            </div>
          </div>
        </div>
      )}

      {booked && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="hotel-booking-success">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
              <BedDouble className="h-7 w-7 text-emerald-600" />
            </div>
            <h3 className="mt-3 text-lg font-extrabold text-emerald-600">Hotel Booking Confirmed!</h3>
            <p className="mt-1 text-sm text-slate-500">Reference: <span className="font-extrabold text-[#1B1F5B]">{booked.pnr}</span></p>
            <p className="mt-1 text-xs text-slate-400">{booked.hotel?.name} | {booked.checkIn} to {booked.checkOut}</p>
            <p className="mt-1 text-xs text-slate-400">Confirmation email sent to {booked.passenger?.email}</p>
            <div className="mt-4 flex justify-center gap-2">
              <button data-testid="hotel-receipt-btn" onClick={() => downloadReceiptPdf({ ...booked, type: "hotel" }, { total: booked.total })}
                className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-600 hover:border-[#1B1F5B]">Download Receipt</button>
              <button data-testid="hotel-success-close-btn" onClick={() => setBooked(null)}
                className="rounded-xl bg-[#1B1F5B] px-8 py-2.5 text-sm font-bold text-white">Done</button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

// ---------------- Holiday Packages ----------------
export const PackagePage = () => {
  const [params] = useSearchParams();
  const city = params.get("city");
  const list = city ? PACKAGES.filter((p) => p.city === city) : PACKAGES;
  return (
    <PageShell
      title={city ? `${city} Tour Packages` : "Holiday Packages"}
      subtitle="Handcrafted holidays with flights, stays & sightseeing"
    >
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((p) => (
          <div key={p.id} className="group overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
            <div className="relative h-48 overflow-hidden">
              <img src={p.img} alt={p.city} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <span className="absolute left-3 top-3 rounded-full bg-[#1B1F5B] px-3 py-1 text-[11px] font-bold text-white">
                {p.nights}N / {p.days}D
              </span>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-1.5 text-base font-extrabold text-slate-800">
                  <Palmtree className="h-4 w-4 text-[#F5B32B]" /> {p.city} Explorer
                </h3>
                <span className="flex items-center gap-1 text-xs font-bold text-emerald-600">
                  <Star className="h-3.5 w-3.5 fill-emerald-500 text-emerald-500" /> {p.rating}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-400">Flights + Hotel + Transfers + Sightseeing</p>
              <div className="mt-3 flex items-center justify-between border-t border-dashed pt-3">
                <p className="text-lg font-extrabold text-[#1B1F5B]">
                  {formatINR(p.price)}
                  <span className="block text-[10px] font-medium text-slate-400">per person</span>
                </p>
                <button
                  onClick={() => toast.success(`${p.city} package enquiry submitted!`)}
                  className="rounded-lg bg-[#F5B32B] px-4 py-2 text-xs font-extrabold text-[#0A0F2E] hover:bg-[#E89A10]"
                >
                  Enquire Now
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </PageShell>
  );
};

// ---------------- Bus ----------------
export const BusPage = () => (
  <PageShell title="Book Bus Tickets" subtitle="Safe & comfortable bus journeys across India">
    <div className="mx-auto mb-8 grid max-w-3xl grid-cols-1 gap-2 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm md:grid-cols-[1fr_1fr_auto]">
      <input placeholder="From city" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
      <input placeholder="To city" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
      <button
        onClick={() => toast.info("Showing popular bus routes below")}
        className="rounded-xl bg-[#1B1F5B] px-8 py-3 text-sm font-bold text-white hover:bg-[#141844]"
      >
        Search
      </button>
    </div>
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {BUS_ROUTES.map((r, i) => (
        <div key={i} className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#1B1F5B]/5">
            <BusIcon className="h-5 w-5 text-[#1B1F5B]" />
          </span>
          <div className="flex-1">
            <p className="text-sm font-bold text-slate-800">{r.from} → {r.to}</p>
            <p className="text-xs text-slate-400">{r.time} | AC Sleeper</p>
          </div>
          <div className="text-right">
            <p className="text-base font-extrabold text-[#1B1F5B]">{formatINR(r.price)}</p>
            <button
              onClick={() => toast.success(`${r.from} → ${r.to} seat enquiry submitted!`)}
              className="mt-1 rounded-md bg-[#F5B32B] px-3 py-1 text-[11px] font-extrabold text-[#0A0F2E] hover:bg-[#E89A10]"
            >
              Book
            </button>
          </div>
        </div>
      ))}
    </div>
  </PageShell>
);

// ---------------- Visa ----------------
export const VisaPage = () => {
  const VISAS = [
    { c: "Dubai (UAE)", t: "3-4 working days", p: 6500 },
    { c: "Singapore", t: "3-5 working days", p: 3200 },
    { c: "Thailand", t: "2-3 working days", p: 4500 },
    { c: "United Kingdom", t: "15-20 working days", p: 13500 },
    { c: "United States", t: "Appointment based", p: 15999 },
    { c: "Schengen (Europe)", t: "10-15 working days", p: 8999 },
  ];
  const [country, setCountry] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "" });
  const submit = () => {
    if (!form.name || !form.phone) { toast.error("Please fill your name and mobile number"); return; }
    apiCreateEnquiry({ type: "visa", name: form.name, phone: form.phone, details: { country } })
      .then(() => { toast.success(`${country} visa enquiry submitted!`); setCountry(null); setForm({ name: "", phone: "" }); })
      .catch(() => toast.error("Could not submit. Please try again."));
  };
  return (
    <PageShell title="Visa Services" subtitle="Fast, reliable & hassle-free visa assistance for every destination">
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {VISAS.map((v, i) => (
          <div key={i} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFF9EC]">
              <FileCheck2 className="h-5 w-5 text-[#E89A10]" />
            </span>
            <h3 className="mt-3 text-base font-extrabold text-slate-800">{v.c} Visa</h3>
            <p className="mt-1 text-xs text-slate-400">Processing: {v.t}</p>
            <div className="mt-3 flex items-center justify-between border-t border-dashed pt-3">
              <p className="text-lg font-extrabold text-[#1B1F5B]">{formatINR(v.p)}</p>
              <button
                data-testid={`visa-apply-${i}`}
                onClick={() => { setCountry(v.c); setForm({ name: "", phone: "" }); }}
                className="rounded-lg bg-[#F5B32B] px-4 py-2 text-xs font-extrabold text-[#0A0F2E] hover:bg-[#E89A10]"
              >
                Apply Now
              </button>
            </div>
          </div>
        ))}
      </div>
      {country && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setCountry(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="visa-enquiry-modal">
            <h3 className="text-lg font-extrabold text-slate-800">{country} Visa Enquiry</h3>
            <p className="mt-1 text-xs text-slate-400">Share your details, our visa expert will call you back.</p>
            <div className="mt-4 space-y-3">
              <input data-testid="visa-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
              <input data-testid="visa-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Mobile number" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
              <button data-testid="visa-submit" onClick={submit} className="w-full rounded-xl bg-[#1B1F5B] py-3 text-sm font-extrabold text-white hover:bg-[#141844]">Submit Enquiry</button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

// ---------------- Group Enquiry ----------------
export const GroupEnquiryPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", from: "", to: "", date: "", pax: 10, notes: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.from || !form.to) {
      toast.error("Please fill name, phone, from & to cities");
      return;
    }
    apiCreateEnquiry({
      type: "group",
      name: form.name,
      phone: form.phone,
      email: form.email,
      details: { from: form.from, to: form.to, date: form.date, pax: form.pax, notes: form.notes },
    })
      .then(() => {
        toast.success("Group enquiry submitted! Our fares team will call you within 2 hours.");
        setForm({ name: "", email: "", phone: "", from: "", to: "", date: "", pax: 10, notes: "" });
      })
      .catch(() => toast.error("Could not submit. Please try again."));
  };
  return (
    <PageShell title="Group Booking Enquiry" subtitle="More than 9 passengers? Get exclusive group fares">
      <div className="mx-auto max-w-2xl rounded-3xl border border-slate-100 bg-white p-6 shadow-[0_15px_50px_rgba(27,31,91,0.10)] md:p-8">
        <div className="mb-5 flex items-center gap-3 rounded-xl bg-[#FFF9EC] p-3.5 text-sm font-semibold text-[#B57A00]">
          <Users className="h-5 w-5" /> Special discounted fares for groups of 10+ passengers
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
          <input value={form.name} onChange={set("name")} placeholder="Your name *" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
          <input value={form.phone} onChange={set("phone")} placeholder="Mobile number *" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
          <input type="email" value={form.email} onChange={set("email")} placeholder="Email address" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
          <input type="date" value={form.date} onChange={set("date")} className="rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-500 outline-none focus:border-[#1B1F5B]" />
          <input value={form.from} onChange={set("from")} placeholder="From city *" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
          <input value={form.to} onChange={set("to")} placeholder="To city *" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
          <input type="number" min={10} value={form.pax} onChange={set("pax")} placeholder="No. of passengers" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
          <textarea value={form.notes} onChange={set("notes")} placeholder="Any special requirement..." rows={3} className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B] md:col-span-2" />
          <button className="flex items-center justify-center gap-2 rounded-xl bg-[#1B1F5B] py-3.5 text-sm font-extrabold tracking-wider text-white transition-colors hover:bg-[#141844] md:col-span-2">
            <Send className="h-4 w-4" /> SUBMIT ENQUIRY
          </button>
        </form>
      </div>
    </PageShell>
  );
};

// ---------------- Generic enquiry pages (Cab / Train / Web check-in) ----------------
export const SimpleEnquiryPage = ({ title, subtitle, icon: Icon, enquiryType = "general" }) => {
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  const submitReq = () => {
    if (!form.name || !form.phone) {
      toast.error("Please fill your name and mobile number");
      return;
    }
    apiCreateEnquiry({ type: enquiryType, name: form.name, phone: form.phone })
      .then(() => {
        setDone(true);
        toast.success("Request submitted successfully!");
      })
      .catch(() => toast.error("Could not submit. Please try again."));
  };
  return (
    <PageShell title={title} subtitle={subtitle}>
      <div className="mx-auto max-w-xl rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-[0_15px_50px_rgba(27,31,91,0.10)]">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#1B1F5B]/5">
          <Icon className="h-8 w-8 text-[#1B1F5B]" />
        </span>
        {done ? (
          <>
            <h2 className="mt-4 text-xl font-extrabold text-emerald-600">Request Received!</h2>
            <p className="mt-2 text-sm text-slate-500">
              Our team will get back to you shortly. For urgent help call{" "}
              <a href={`tel:${CONTACT.phone}`} className="font-bold text-[#1B1F5B]">{CONTACT.phonePretty}</a>
            </p>
          </>
        ) : (
          <>
            <h2 className="mt-4 text-xl font-extrabold text-slate-800">{title}</h2>
            <p className="mt-2 text-sm text-slate-500">{subtitle}. Submit your details and our travel expert will assist you.</p>
            <div className="mt-5 space-y-3">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]"
              />
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Mobile number"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]"
              />
              <button
                onClick={submitReq}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1B1F5B] py-3 text-sm font-extrabold text-white hover:bg-[#141844]"
              >
                <CalendarDays className="h-4 w-4" /> Request Callback
              </button>
            </div>
          </>
        )}
      </div>
    </PageShell>
  );
};
